import { Action, Dispatch } from 'redux';

import {
  USER_PREFERENCES_URL,
  ACCOUNT_PREFERENCES_URL,
  RequestId,
  API_DEFAULT_RETRIES,
  KEVALIN_COLLECTIONS,
} from 'appConstants';
import {
  ApiRequest,
  history,
  MailUtils,
  TicketPreferencesUtils,
  KevalinUtils,
} from 'services';
import { closeOptInModal, store } from 'reducers';
import {
  UserTicketPreference,
  AccountTicketPreference,
  TicketPreference,
  SurveyFeedback,
} from 'types';

export interface TicketPreferencesAction extends Action {
  readonly issuetrak?: boolean;
  readonly userOptIn?: boolean | null;
  readonly accountOptIn?: boolean;
  readonly userPrefError?: boolean;
  readonly accountPrefError?: boolean;
  readonly updateUserPrefError?: boolean;
}

const initialState = {
  userPrefError: false,
  accountPrefError: false,
  updateUserPrefError: false,
} as TicketPreference;

enum Actions {
  SHOW_USER_PREFERENCE_ERROR = 'SHOW_USER_PREFERENCE_ERROR',
  SHOW_ACCOUNT_PREFERENCE_ERROR = 'SHOW_ACCOUNT_PREFERENCE_ERROR',
  SET_USER_PREFERENCE = 'SET_USER_PREFERENCE',
  SET_ACCOUNT_PREFERENCE = 'SET_ACCOUNT_PREFERENCE',
  SHOW_UPDATE_USER_PREFERENCE_ERROR = 'SHOW_UPDATE_USER_PREFERENCE_ERROR',
}

const setUserPreferences = (
  userOptIn: boolean | null,
): TicketPreferencesAction => ({
  type: Actions.SET_USER_PREFERENCE,
  userOptIn,
});

const setAccountPreferences = (
  accountOptIn: boolean,
  issuetrak: boolean,
): TicketPreferencesAction => ({
  type: Actions.SET_ACCOUNT_PREFERENCE,
  accountOptIn,
  issuetrak,
});

const showUserPreferenceError = (): TicketPreferencesAction => ({
  type: Actions.SHOW_USER_PREFERENCE_ERROR,
});

const showAccountPreferenceError = (): TicketPreferencesAction => ({
  type: Actions.SHOW_ACCOUNT_PREFERENCE_ERROR,
});

const showUpdateUserPreferencesError = (): TicketPreferencesAction => ({
  type: Actions.SHOW_UPDATE_USER_PREFERENCE_ERROR,
});

const ticketPreferencesReducer = (
  state = initialState,
  action: TicketPreferencesAction,
) => {
  switch (action.type) {
    case Actions.SET_USER_PREFERENCE:
      return {
        ...state,
        userOptIn: action.userOptIn,
      };
    case Actions.SET_ACCOUNT_PREFERENCE:
      return {
        ...state,
        accountOptIn: action.accountOptIn,
        issuetrak: action.issuetrak,
      };
    case Actions.SHOW_USER_PREFERENCE_ERROR:
      return {
        ...state,
        userPrefError: true,
      };
    case Actions.SHOW_ACCOUNT_PREFERENCE_ERROR:
      return {
        ...state,
        accountPrefError: true,
      };
    case Actions.SHOW_UPDATE_USER_PREFERENCE_ERROR:
      return {
        ...state,
        updateUserPrefError: true,
      };
    default:
      return state;
  }
};

/** GET user-preferences */
const userPreference = () => async (
  dispatch: Dispatch<TicketPreferencesAction>,
) => {
  const onError = () => {
    dispatch(showUserPreferenceError());
  };

  const onSuccess = (response: UserTicketPreference) => {
    dispatch(setUserPreferences(response['opt-in']));
  };

  return ApiRequest.get(`${USER_PREFERENCES_URL}`, {
    dispatchId: RequestId.GET_USER_PREFERENCES,
    onSuccess,
    onError,
  });
};

/** GET account-preferences */
const accountPreference = (tenantId: String) => async (
  dispatch: Dispatch<TicketPreferencesAction>,
) => {
  const onError = () => {
    dispatch(showAccountPreferenceError());
  };

  const onSuccess = (response: AccountTicketPreference) => {
    dispatch(setAccountPreferences(response['opt-in'], response.issuetrak));
  };

  return ApiRequest.get(`${ACCOUNT_PREFERENCES_URL}`, {
    headers: { 'Account-ID': `${tenantId}` },
    dispatchId: RequestId.GET_ACCOUNT_PREFERENCES,
    onSuccess,
    onError,
    retries: API_DEFAULT_RETRIES,
  });
};

const storePreferenceMetrics = (
  optIn: boolean,
  feedback?: SurveyFeedback,
) => async (dispatch: Dispatch<TicketPreferencesAction>) => {
  const payload = {
    eventData: {
      ...MailUtils.setSurveyPayload(feedback),
      eventType: optIn ? 'OPTIN' : 'OPTOUT',
    },
  };
  return ApiRequest.post(
    KevalinUtils.getKevalinURL(KEVALIN_COLLECTIONS.CTUI_PREFERENCES),
    payload,
    {
      retries: API_DEFAULT_RETRIES,
    },
  );
};

/** Update user-preferences on Opt-in button click */
const updateUserPreferences = (
  optInValue: boolean,
  feedback?: SurveyFeedback,
) => async (dispatch: Dispatch<TicketPreferencesAction>) => {
  const payload = {
    'opt-in': optInValue,
  };

  const onError = () => {
    dispatch(showUpdateUserPreferencesError());
  };

  const onSuccess = async () => {
    if (optInValue) {
      await dispatch(storePreferenceMetrics(true));
      history.push('/list');
      window.location.replace('list');
      store.dispatch(closeOptInModal());
    } else {
      await dispatch(storePreferenceMetrics(false, feedback));
      localStorage.setItem('hasUserOptedOutOfBeta', 'true');
      /** Redirects to MyRack */
      TicketPreferencesUtils.redirectMyrack('list');
    }
  };

  return ApiRequest.put(USER_PREFERENCES_URL, payload, {
    onSuccess,
    onError,
    retries: API_DEFAULT_RETRIES,
  });
};

export {
  ticketPreferencesReducer,
  userPreference,
  accountPreference,
  setUserPreferences,
  setAccountPreferences,
  storePreferenceMetrics,
  updateUserPreferences,
};
